const cache = new Map;
const links = new Set;

const defaultOptions = {
    prefetch: true,
    transitions: {
        default: {
            async onLeave(wrapper)
            {
            },

            async leave(wrapper)
            {
            },

            async enter(wrapper)
            {
            },

            async onEnter(wrapper)
            {
            }
        }
    },
    http: {
        headers: {},
        options: {},
    },
    ignoreClasses: [],
};

let url = new URL( window.location.href );
cache.set( url.href, document.cloneNode(true) );

class TransitionManager {
    wrapper;

    options = {};

    popping = false;

    running = false;

    fetching = false;

    registered = false;

    changeCallback = () => {};

    constructor() {
        window.addEventListener("popstate", async event => {
            this.popping = true;

            await this.navigate( event.target.location.href, event.state?.transition ?? 'default', 'popstate', event.state );

            setTimeout( () => {
                window.scrollTo( {
                    left: event.state?.scrollX ?? 0,
                    top: event.state?.scrollY ?? 0,
                    behavior: 'instant'
                } );
            } );

            this.popping = false;
        });
    }

    register(newOptions) {
        if ( this.registered ) {
            return;
        }

        this.options = Object.assign( defaultOptions, newOptions );

        if ( this.options.wrapper ) {
            this.setWrapper( this.options.wrapper );
        }

        if ( this.options.prefetch === false ) {
            this.clearCache();
        }

        this.registered = true;

        return this;
    }

    addLink(linkElement) {
        links.add( linkElement );

        return this;
    }

    removeLink(linkElement) {
        links.delete( linkElement );

        return this;
    }

    setWrapper(wrapperElement) {
        this.wrapper = wrapperElement;

        return this;
    }

    clearCache() {
        cache.clear();

        return this;
    }

    async fetch(newLocation, trigger, prefetch = true) {
        prefetch = prefetch && this.options.prefetch;

        if ( url.origin !== newLocation.origin ||
            trigger === 'mouseover' && url.href === newLocation.href ||
            trigger === 'mouseover' && prefetch === false ) {
            return;
        }

        const fetchPageContent = async url => ( await this.options.fetch( url ) );

        if ( !prefetch ) {
            return await fetchPageContent( newLocation.href );
        }

        if ( !cache.has( newLocation.href ) ) {
            cache.set( newLocation.href, await fetchPageContent( newLocation.href ) );
        }

        if ( trigger !== 'mouseover' ) {
            return cache.get( newLocation.href );
        }
    }

    async navigate(newLocation, transition_name = 'default', trigger = 'script', state = {}) {
        const link = state?.link;

        if ( typeof newLocation === 'string' ) {
            newLocation = new URL( newLocation, url );
        }

        if ( url.origin !== newLocation.origin ) {
            window.location.href = newLocation.href;
            return;
        }

        if ( this.running || trigger !== 'popstate' && url.href === newLocation.href ) {
            return;
        }

        if ( !this.options.transitions[ transition_name ] ) {
            console.error("ERROR: Unknown transition with name '" + transition_name + "'");
            transition_name = 'default';
        }

        const transition = this.options.transitions[ transition_name ];

        const wrapper = this.wrapper;

        url = newLocation;

        if ( transition && transition.onLeave ) {
            await transition.onLeave( { link, links, wrapper, url, } );
        }

        this.running = true;

        if ( !this.popping ) {
            history.pushState({
                transition: transition_name,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
            }, "", newLocation );
        }

        if ( transition && transition.leave ) {
            await transition.leave( { link, links, wrapper, url, } );
        }

        const html = await this.fetch( newLocation, trigger );

        const generateQuerySelector = el => {
            if ( el.tagName.toLowerCase() === 'body' ) return 'body';

            return el.id ? '#' + el.getAttribute( 'id' ) : el.tagName;
        }

        const classes = Array.from( document.body.classList );
        const newClasses = Array.from( html.body.classList );
        const classesToRemove = classes.filter( x => !newClasses.includes( x ) );
        const classesToAdd = newClasses.filter( x => !classes.includes( x ) && !this.options.ignoreClasses.includes( x ) );

        classesToRemove.forEach( item => document.body.classList.remove( item ) );
        classesToAdd.forEach( newItem => document.body.classList.add( newItem ) );

        document.title = html.querySelector('title').innerText;

        wrapper.innerHTML = html.querySelector( generateQuerySelector( wrapper ) ).innerHTML;

        if ( transition && transition.enter ) {
            await transition.enter( { link, links, wrapper, url, } );
        }

        this.changeCallback( { link, links, wrapper, url, } );

        if ( transition && transition.onEnter ) {
            await transition.onEnter( { link, links, wrapper, url, } );
        }

        this.running = false;

        return this;
    }

    async handleMouseover(event) {
        const link = event.currentTarget;

        const prefetch = !link.hasAttribute('prefetch') || link.getAttribute('prefetch') === 'true';

        await this.fetch( new URL( link.href, window.location.origin ), 'mouseover', prefetch );

        return this;
    }

    async handleClick(event) {
        if ( ! ( event.metaKey || event.ctrlKey ) ) {
            const link = event.currentTarget;

            event.preventDefault();

            const transition = link.hasAttribute('transition') ? link.getAttribute('transition') : 'default';

            await this.navigate( new URL( link.href, window.location.origin ), transition, 'click', {link, transition} );
        }

        return this;
    }

    onChange(callback) {
        this.changeCallback = callback;

        return this;
    }
}

const transitionManager = new TransitionManager;

export default transitionManager;

export {
    transitionManager,
}